import {
  IAntifraudApiEvent,
  IAntifraudParticipant,
  IAntifraudParticipantsResponse,
  IAntifraudProcess,
  IKYCField,
  IAntifraudStepSummary,
  IKYCParticipantStatus,
  _antifraudParticipantStatus,
  _antifraudStepDecision,
  _participantStepOutcome,
  _antifraudProcessStatus,
  _participantRole,
  _resendableSolution
} from './antifraudAnalysisTypes'

const DESCRIPTIONS: {
  [key in Exclude<_antifraudProcessStatus, 'unmapped' | 'notStarted'>]: string[]
} = {
  approved: ['aprovado', 'concluido', 'concluído'],
  lowRisk: ['risco baixo'],
  reproved: [
    'reprovado',
    'análise manual',
    'analise manual',
    'em divergência',
    'em divergencia',
    'cancelado',
    'expirado'
  ],
  retryable: ['erro'],
  waiting: [
    'pendente',
    'em processamento',
    'aguardando execução',
    'aguardando execucao',
    'inconclusivo',
    'não informado',
    'nao informado',
    'desligado'
  ],
  noAnswer: ['erro ao chamar api kyc']
}

export function classifyDescription (description?: string): _antifraudProcessStatus {
  const text = (description ?? '').trim().toLowerCase()

  if (!text) return 'noAnswer'

  if (text.includes('não mapeado') || text.includes('nao mapeado')) return 'unmapped'

  if (DESCRIPTIONS.noAnswer.some(term => text.includes(term))) return 'noAnswer'
  if (DESCRIPTIONS.retryable.some(term => text.includes(term))) return 'retryable'
  if (DESCRIPTIONS.reproved.some(term => text.includes(term))) return 'reproved'
  if (DESCRIPTIONS.lowRisk.some(term => text.includes(term))) return 'lowRisk'
  if (DESCRIPTIONS.approved.some(term => text.includes(term))) return 'approved'
  if (DESCRIPTIONS.waiting.some(term => text.includes(term))) return 'waiting'

  return 'waiting'
}

const ACERTPIX_STATUS: { [code: number]: _antifraudProcessStatus } = {
  2: 'waiting',
  3: 'reproved',
  4: 'approved',
  6: 'reproved',
  7: 'reproved',
  11: 'reproved',
  12: 'reproved',
  30: 'reproved'
}

function classifyField (field?: IKYCField): _antifraudProcessStatus {
  return classifyDescription(field?.description)
}

export function classifyAcertPix (field?: IKYCField): _antifraudProcessStatus {
  if (typeof field?.score !== 'number' || field.score === 0) {
    return classifyField(field)
  }

  return ACERTPIX_STATUS[field.score] ?? 'unmapped'
}

export function buildDescription (field?: IKYCField): string {
  const description = (field?.description ?? '').trim()

  if (!description) return 'Sem retorno'
  if (typeof field?.score !== 'number') return description

  return `${field.score} — ${description}`
}

export function hasAnyAntifraudResult (processes: IAntifraudProcess[]): boolean {
  return processes.some(process => (
    ['approved', 'lowRisk', 'reproved', 'retryable', 'unmapped'].includes(process.status)
  ))
}

const UNICO_STATUS_TYPE = 'Reconhecimento facial'

const EXPIRED_LABEL = 'Expirado'

function describesExpiry (process?: IAntifraudProcess): boolean {
  return process?.description.toLowerCase().includes('expirado') ?? false
}

export function unicoExpiredByReturn (processes: IAntifraudProcess[]): boolean {
  return describesExpiry(processes.find(process => (
    process.solution === 'Único' && process.type === UNICO_STATUS_TYPE
  )))
}

function propagateExpiry (processes: IAntifraudProcess[]): IAntifraudProcess[] {
  if (!unicoExpiredByReturn(processes)) return processes

  return processes.map(process => (
    process.status === 'waiting'
      ? { ...process, status: 'reproved' as const, description: EXPIRED_LABEL }
      : process
  ))
}

function unicoNeverStarted (processes: IAntifraudProcess[]): boolean {
  const unico = processes.filter(process => process.solution === 'Único')

  return Boolean(unico.length) && unico.every(process => process.status === 'notStarted')
}

function unicoAwaitingClient (processes: IAntifraudProcess[]): boolean {
  const unico = processes.filter(process => process.solution === 'Único')

  return !hasAnyAntifraudResult(unico) && unico.some(process => process.status === 'waiting')
}

const AWAITING_CLIENT_LABEL = 'Aguardando o cliente'

const AWAITING_OCR_LABEL = 'Aguardando OCR'

function awaitingClientWhenPending (processes: IAntifraudProcess[]): IAntifraudProcess[] {
  if (hasAnyAntifraudResult(processes)) return processes

  return processes.map(process => (
    process.status === 'waiting'
      ? { ...process, description: AWAITING_CLIENT_LABEL }
      : process
  ))
}

export function unicoIsExpired (expiresAt?: string, now = Date.now()): boolean {
  if (!expiresAt) return false

  const deadline = new Date(expiresAt).getTime()

  if (Number.isNaN(deadline)) return false

  return now > deadline
}

const WITHOUT_RESULT: _antifraudProcessStatus[] = ['noAnswer', 'notStarted', 'waiting', 'retryable']

function buildProcess (
  solution: IAntifraudProcess['solution'],
  type: string,
  field: IKYCField | undefined,
  options: {
    isV2?: boolean
    started?: boolean
    resendKey?: _resendableSolution
    resendable?: boolean
    waitingLabel?: string
    classify?: (field?: IKYCField) => _antifraudProcessStatus
  } = {}
): IAntifraudProcess {
  const classify = options.classify ?? classifyField
  const classified = classify(field)

  const isSilent = !(field?.description ?? '').trim()
  const silentStatus = options.started ? 'waiting' : 'notStarted'
  const status = classified === 'noAnswer' && isSilent ? silentStatus : classified

  const description = status === 'notStarted'
    ? 'Não iniciado'
    : status === 'waiting' && options.waitingLabel
      ? options.waitingLabel
      : buildDescription(field)

  return {
    solution,
    type,
    status,
    description,
    isV2: options.isV2,
    resendKey: options.resendKey,
    canResend: WITHOUT_RESULT.includes(status) &&
      Boolean(options.resendKey) &&
      options.resendable !== false &&
      !(status === 'waiting' && options.waitingLabel)
  }
}

export function buildProcesses (
  status: IKYCParticipantStatus,
  options: { dispatched?: boolean } = {}
): IAntifraudProcess[] {
  const started = (solutionStarted?: boolean) => Boolean(options.dispatched ?? solutionStarted)

  const unico = { started: Boolean(status.unico?.startedProcess) }

  const unicoReturns = [
    buildProcess('Único', UNICO_STATUS_TYPE, status.unico?.status, unico),
    buildProcess('Único', 'Prova de vida', status.unico?.liveness, unico),
    buildProcess('Único', 'Facematch', status.unico?.facematch, unico)
  ]

  const unicoProcesses = propagateExpiry(awaitingClientWhenPending(unicoReturns))

  const acertPix = {
    started: Boolean(status.acertPix?.startedProcess),
    classify: classifyAcertPix,
    waitingLabel: AWAITING_OCR_LABEL,
    resendKey: 'acertPix' as const,
    resendable: !unicoNeverStarted(unicoProcesses) && !unicoAwaitingClient(unicoProcesses)
  }
  const emailAge = { started: started(status.emailAge?.startedProcess), resendKey: 'emailAge' as const }

  const acertPixProcess = buildProcess('AcertPix GED', 'Reconhecimento de RG/CNH', status.acertPix?.acertPix, acertPix)

  return [
    ...unicoProcesses,
    {
      ...acertPixProcess,
      resendAfterUnico: Boolean(acertPixProcess.canResend) && hasAnyAntifraudResult(unicoProcesses)
    },

    buildProcess('Email Age', 'Validação de Email', status.emailAge?.fraudRisk, emailAge),
    buildProcess('Email Age', 'Validação de dados complementares', status.emailAge?.overallDigitalIdentityScore, emailAge)
  ]
}

const PROCESS_SOLUTION: { [solution in _resendableSolution]: IAntifraudProcess['solution'] } = {
  unico: 'Único',
  emailAge: 'Email Age',
  acertPix: 'AcertPix GED'
}

const COMMUNICATION_FAILURE_LABEL = 'Falha na comunicação'

// APIs cuja chamada mais recente do histórico terminou com erro: disparo depois das três tentativas,
// reconsulta, reenvio ou OCR que o core não conseguiu abrir
export function failedApiSolutions (events: IAntifraudApiEvent[] = []): _resendableSolution[] {
  const latest = new Map<_resendableSolution, IAntifraudApiEvent>()

  ;[...events]
    .sort((first, second) => first.createdAt.localeCompare(second.createdAt))
    .forEach(event => latest.set(event.solution, event))

  return Array.from(latest.values())
    .filter(event => Boolean(event.error))
    .map(event => event.solution)
}

// A linha da API que falhou mostra a falha e o reenvio dela: o Único pelo botão do topo, o EmailAge
// sozinho, e o AcertPix só com o Único já retornado, porque o OCR abre depois dele
export function markCommunicationFailures (
  processes: IAntifraudProcess[],
  failed: _resendableSolution[]
): IAntifraudProcess[] {
  if (!failed.length) return processes

  const failedSolutions = failed.map(solution => PROCESS_SOLUTION[solution])

  const marked = processes.map(process => {
    if (!failedSolutions.includes(process.solution)) return process

    return {
      ...process,
      status: 'noAnswer' as const,
      description: COMMUNICATION_FAILURE_LABEL,
      communicationError: true,
      canResend: Boolean(process.resendKey)
    }
  })

  const unicoReturned = hasAnyAntifraudResult(marked.filter(process => process.solution === 'Único'))

  return marked.map(process => (
    process.solution === 'AcertPix GED' && process.communicationError
      ? { ...process, canResend: unicoReturned, resendAfterUnico: unicoReturned }
      : process
  ))
}

function communicationFailureBanner (processes: IAntifraudProcess[]): IAntifraudParticipant['banner'] {
  const solutions = Array.from(new Set(
    processes.filter(process => process.communicationError).map(process => process.solution)
  ))

  if (!solutions.length) return undefined

  return {
    type: 'error',
    title: `Falha na comunicação com ${solutions.join(' e ')}.`,
    description: 'As tentativas automáticas falharam (erro do fornecedor ou tempo esgotado). Use o reenvio manual da solução ou registre a validação manual para liberar a etapa.'
  }
}

const AUTOMATIC_REPROVAL_SOLUTIONS: IAntifraudProcess['solution'][] = ['Único', 'Email Age']

// Igual ao line-server: só o Único e o EmailAge reprovados juntos reprovam sozinhos; uma reprova só,
// ou o link expirado, pede o parecer da Formalização
export function automaticallyRejected (participant: Pick<IAntifraudParticipant, 'processes'>): boolean {
  const processes = participant.processes ?? []

  return AUTOMATIC_REPROVAL_SOLUTIONS.every(solution => processes.some(process => (
    process.solution === solution &&
    process.status === 'reproved' &&
    !describesExpiry(process)
  )))
}

export function resolveParticipantStatus (
  processes: IAntifraudProcess[]
): _antifraudParticipantStatus {
  if (automaticallyRejected({ processes })) return 'reproved'
  if (processes.some(process => process.status === 'reproved')) return 'partialReproval'
  if (processes.some(process => process.status === 'retryable')) return 'retryable'
  if (processes.some(process => process.status === 'noAnswer')) return 'communicationFailure'
  if (!hasAnyAntifraudResult(processes)) return 'awaitingReturn'

  if (processes.some(process => process.status === 'unmapped')) return 'needsReview'

  if (unicoAwaitingClient(processes)) return 'waitingClient'

  // Com o Único concluído, o que falta não depende mais do cliente
  if (processes.some(process => process.solution === 'AcertPix GED' && process.status === 'notStarted')) {
    return 'ocrNotStarted'
  }
  if (processes.some(process => ['waiting', 'notStarted'].includes(process.status))) {
    return 'awaitingReturn'
  }
  if (processes.some(process => process.status === 'lowRisk')) return 'lowRisk'

  return 'approved'
}

const WARNABLE_STATUSES: _antifraudParticipantStatus[] = ['approved', 'lowRisk']

export function showsFraudWarning (participant: IAntifraudParticipant): boolean {
  return Boolean(participant.alerts?.length) && WARNABLE_STATUSES.includes(participant.status)
}

const REVIEW_TAB_STATUS: { [review: string]: _antifraudParticipantStatus } = {
  approve: 'approved',
  approveWithRestriction: 'approved',
  reject: 'reproved'
}

export function participantTabStatus (
  participant: IAntifraudParticipant
): _antifraudParticipantStatus {
  const review = participant.savedReview?.review

  return (review && REVIEW_TAB_STATUS[review]) || participant.status
}

// Só aprova sozinho com todos os retornos aprovados; risco baixo segue para a validação manual
const AUTOMATICALLY_APPROVED: _antifraudParticipantStatus[] = ['approved']

const AWAITING_RETURN: _antifraudParticipantStatus[] = ['awaitingReturn', 'waitingClient', 'ocrNotStarted', 'communicationFailure', 'retryable']

export function participantStepOutcome (
  participant: IAntifraudParticipant
): _participantStepOutcome {
  const review = participant.savedReview?.review

  if (review) return review === 'reject' ? 'rejected' : 'approved'

  if (automaticallyRejected(participant)) return 'rejected'

  return AUTOMATICALLY_APPROVED.includes(participant.status) ? 'approved' : 'manualAnalysis'
}

// Igual ao line-server: todos aprovados aprovam, todos reprovados reprovam, o resto fica em análise
function resolveStepDecision (
  rejected: number,
  blocked: number,
  approved: number
): _antifraudStepDecision {
  if (blocked) return 'analysis'
  if (rejected && !approved) return 'reproved'
  if (approved && !rejected) return 'approved'

  return 'analysis'
}

export function summarizeAntifraudStep (
  participants: IAntifraudParticipant[]
): IAntifraudStepSummary {
  const outcomes = participants.map(participant => ({
    participant,
    outcome: participantStepOutcome(participant)
  }))

  const labelsWithOutcome = (outcome: _participantStepOutcome) => outcomes
    .filter(current => current.outcome === outcome)
    .map(current => current.participant.tab)

  const approved = labelsWithOutcome('approved')
  const rejected = labelsWithOutcome('rejected')

  const byReview = outcomes
    .filter(current => current.outcome !== 'manualAnalysis' && Boolean(current.participant.savedReview?.review))
    .map(current => current.participant.tab)

  const blocked = outcomes.filter(current => current.outcome === 'manualAnalysis')

  const awaitingReturn = blocked
    .filter(current => AWAITING_RETURN.includes(current.participant.status))
    .map(current => current.participant.tab)

  const awaitingReview = blocked
    .filter(current => !AWAITING_RETURN.includes(current.participant.status))
    .map(current => current.participant.tab)

  return {
    decision: resolveStepDecision(rejected.length, blocked.length, approved.length),
    approved,
    rejected,
    byReview,
    awaitingReview,
    awaitingReturn
  }
}

function unicoProcesses (processes: IAntifraudProcess[]): IAntifraudProcess[] {
  return processes.filter(process => process.solution === 'Único')
}



export function canReconcileUnico (
  processes: IAntifraudProcess[],
  options: { expired?: boolean } = {}
): boolean {
  const unico = unicoProcesses(processes)

  if (!unico.length || unicoNeverStarted(processes)) return false
  if (unico.some(process => process.communicationError)) return false
  if (hasAnyAntifraudResult(unico)) return false

  return unico.some(process => process.status === 'noAnswer') || Boolean(options.expired)
}

// As duas linhas do EmailAge vêm da mesma consulta: um reenvio só atualiza as duas
export function canResendEmailAge (processes: IAntifraudProcess[]): boolean {
  return processes.some(process => process.resendKey === 'emailAge' && process.canResend)
}

function formatTimestamp (status: IKYCParticipantStatus): string {
  const dates = [
    status.unico?.responseDate,
    status.acertPix?.responseDate,
    status.emailAge?.responseDate
  ].filter(Boolean) as string[]

  if (!dates.length) return 'Sem retorno das APIs até o momento'

  const newest = dates
    .map(date => new Date(date))
    .filter(date => !isNaN(date.getTime()))
    .sort((first, second) => second.getTime() - first.getTime())[0]

  if (!newest) return 'Sem retorno das APIs até o momento'

  return `Último retorno em ${newest.toLocaleDateString('pt-BR')} às ${newest.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}

function buildBanner (
  status: _antifraudParticipantStatus
): IAntifraudParticipant['banner'] {
  switch (status) {
    case 'reproved':
      return {
        type: 'error',
        title: 'Participante reprovado no antifraude.',
        description: 'A Formalização foi notificada por e-mail. Registre um parecer da área para aprovar ou rejeitar o participante.'
      }
    case 'partialReproval':
      return {
        type: 'warning',
        title: 'Reprova em parte das validações.',
        description: 'O participante só é reprovado automaticamente com o Único e o Email Age reprovados. A Formalização foi notificada por e-mail. Registre um parecer da área para aprovar ou rejeitar o participante.'
      }
    case 'retryable':
      return {
        type: 'warning',
        title: 'A execução da validação falhou.',
        description: 'O fornecedor encerrou o processo com erro, e não é um veredito sobre o participante. Reenvie para gerar uma nova tentativa.'
      }
    case 'communicationFailure':
      return {
        type: 'error',
        title: 'Alguma validação não obteve retorno.',
        description: 'Reenvie a solução que falhou ou registre a validação manual para liberar a etapa.'
      }
    case 'needsReview':
      return {
        type: 'info',
        title: 'Retorno com código que o sistema não sabe interpretar.',
        description: 'A solução respondeu com um status fora da tabela conhecida. Confira o retorno e registre a validação manual para liberar a etapa.'
      }
    case 'awaitingReturn':
      return {
        type: 'info',
        title: 'Consultas disparadas, ainda sem retorno.',
        description: 'As validações foram enviadas às soluções e nenhuma resposta chegou até agora. Não é necessário reenviar.'
      }
    case 'ocrNotStarted':
      return {
        type: 'warning',
        title: 'O OCR do documento não foi aberto.',
        description: 'O Único foi concluído, mas o AcertPix não chegou a ler o RG/CNH. Reenvie o AcertPix para abrir o OCR.'
      }
    case 'waitingClient':
      return {
        type: 'info',
        title: 'Aguardando o cliente concluir as validações.',
        description: 'O participante recebeu o link por WhatsApp e SMS. Passado o prazo do link, consulte o Único para saber o que aconteceu.'
      }
    default:
      return undefined
  }
}

export function mapParticipants (
  response?: IAntifraudParticipantsResponse | null
): IAntifraudParticipant[] {
  if (!response) return []

  const personFor = (participantRef: string) => response.people?.[participantRef] ?? {
    areaName: participantRef,
    label: participantRef,
    name: participantRef,
    cpf: ''
  }

  const apiEventsFor = (participantRef: string) => response.apiEvents?.[participantRef] ?? []

  const returned: IAntifraudParticipant[] = (response.participants ?? []).map(participant => {
    const processes = markCommunicationFailures(
      buildProcesses(participant.status ?? {}, { dispatched: true }),
      failedApiSolutions(apiEventsFor(participant.participantRef))
    )
    const status = resolveParticipantStatus(processes)
    const person = personFor(participant.participantRef)

    return {
      id: participant.participantRef,
      ref: participant.participantRef,
      tab: person.label ?? person.areaName,
      name: person.name,
      cpf: person.cpf,
      role: person.label ?? person.areaName,
      status,
      alerts: participant.status?.emailAge?.alerts ?? [],
      timestamp: formatTimestamp(participant.status ?? {}),
      unicoExpired: unicoIsExpired(participant.status?.unico?.expiresAt),
      banner: communicationFailureBanner(processes) ?? buildBanner(status),
      person,
      savedReview: response.reviews?.[participant.participantRef],
      reviewHistory: response.reviewHistory?.[participant.participantRef] ?? [],
      apiEvents: apiEventsFor(participant.participantRef),
      processes
    }
  })

  const pending: IAntifraudParticipant[] = (response.pending ?? []).map(participant => {
    const person = personFor(participant.participantRef)
    const processes = markCommunicationFailures(
      buildProcesses({}),
      failedApiSolutions(apiEventsFor(participant.participantRef))
    )

    return {
      alerts: [],
      id: participant.participantRef,
      ref: participant.participantRef,
      tab: participant.label ?? participant.areaName,
      name: person.name,
      cpf: person.cpf,
      role: participant.label ?? participant.areaName,
      status: 'communicationFailure',
      person,
      savedReview: response.reviews?.[participant.participantRef],
      reviewHistory: response.reviewHistory?.[participant.participantRef] ?? [],
      apiEvents: apiEventsFor(participant.participantRef),
      timestamp: 'Antifraude ainda não iniciado',
      banner: communicationFailureBanner(processes) ?? {
        type: 'error',
        title: 'Antifraude não iniciado para este participante.',
        description: 'O disparo falhou ou o cadastro está incompleto. Reenvie para tentar novamente.'
      },
      processes
    }
  })

  return sortWithSpousesAfterPrincipals([...returned, ...pending])
}

const EMITENTE_REF = 'titular'

const ROLE_BY_REF: { [ref: string]: _participantRole } = {
  titular: 'emitente',
  a: 'pessoa',
  b: 'pessoa',
  c: 'pessoa',
  d: 'pessoa'
}

const ROLE_BY_PREFIX: Array<{ prefix: string, role: _participantRole }> = [
  { prefix: 'conjuge', role: 'conjuge' },
  { prefix: 'alienante', role: 'alienante' },
  { prefix: 'garantidor', role: 'garantidor' },
  { prefix: 'proprietario', role: 'proprietario' },
  { prefix: 'terceiro', role: 'terceiro' },
  { prefix: 'socio', role: 'socio' }
]

export function participantRole (ref: string): _participantRole {
  if (ROLE_BY_REF[ref]) return ROLE_BY_REF[ref]

  const found = ROLE_BY_PREFIX.find(entry => ref.startsWith(entry.prefix))

  return found?.role ?? 'outro'
}

export function principalOf (ref: string): string | undefined {
  if (ref === 'conjuge') return EMITENTE_REF
  if (!ref.startsWith('conjuge-')) return undefined

  return ref.slice('conjuge-'.length)
}

export function sortWithSpousesAfterPrincipals (
  participants: IAntifraudParticipant[]
): IAntifraudParticipant[] {
  const spousesByPrincipal = new Map<string, IAntifraudParticipant[]>()
  const principals: IAntifraudParticipant[] = []

  for (const participant of participants) {
    const principal = principalOf(participant.ref)

    if (!principal) {
      principals.push(participant)
      continue
    }

    spousesByPrincipal.set(principal, [...(spousesByPrincipal.get(principal) ?? []), participant])
  }

  const ordered = [
    ...principals.filter(participant => participant.ref === EMITENTE_REF),
    ...principals.filter(participant => participant.ref !== EMITENTE_REF)
  ].flatMap(participant => [
    participant,
    ...(spousesByPrincipal.get(participant.ref) ?? [])
  ])

  const semPrincipal: IAntifraudParticipant[] = []

  spousesByPrincipal.forEach((spouses, principal) => {
    if (principals.some(participant => participant.ref === principal)) return

    semPrincipal.push(...spouses)
  })

  return [...ordered, ...semPrincipal]
}
